import { Button } from '@components'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaCheckCircle, FaTimes } from 'react-icons/fa'
import styled from 'styled-components'

interface CompleteToastProps {
  title: string
  description: string
  descriptionContinued?: string
  actionLabel?: string
  onAction?: () => void
  redirect?: React.ReactNode
}

interface Props extends CompleteToastProps {
  toastId: string
}

export const showCompleteToast = ({
  title,
  description,
  descriptionContinued,
  actionLabel,
  onAction,
  redirect
}: CompleteToastProps): void => {
  toast.custom(
    toast => (
      <CompleteSlide
        title={title}
        description={description}
        descriptionContinued={descriptionContinued}
        actionLabel={actionLabel}
        onAction={onAction}
        redirect={redirect}
        toastId={toast.id}
      />
    ),
    {
      position: 'bottom-right',
      duration: Infinity
    }
  )
}

export const CompleteSlide: React.FC<Props> = ({
  toastId,
  title,
  description,
  descriptionContinued,
  actionLabel,
  onAction,
  redirect
}) => {
  const { t } = useTranslation()
  const [isVisible, setVisible] = useState(true)

  const dismiss = () => {
    setVisible(false)
    setTimeout(() => {
      toast.remove(toastId)
    }, 500)
  }

  return (
    <AnimatePresence initial={false}>
      {isVisible && (
        <StyledToastContent key={`toast-${toastId}`}>
          {!redirect && (
            <CloseButton type="button" aria-label={t('general.close')} onClick={dismiss}>
              <FaTimes />
            </CloseButton>
          )}
          <h3>
            <FaCheckCircle />
            {title}
          </h3>
          <p>{description}</p>
          {descriptionContinued && <p>{descriptionContinued}</p>}

          {redirect ? (
            redirect
          ) : actionLabel ? (
            <Button
              type="button"
              intent="primary"
              onClick={() => {
                onAction?.()
                dismiss()
              }}
            >
              {actionLabel}
            </Button>
          ) : (
            <Button type="button" intent="success" icon={<FaCheck />} onClick={dismiss}>
              {t('general.ok')}
            </Button>
          )}
        </StyledToastContent>
      )}
    </AnimatePresence>
  )
}

const BORDER_RADIUS = '6px'

const StyledToastContent = styled(motion.div).attrs({
  initial: false,
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 }
})`
  position: relative;
  padding: ${({ theme }) => theme.spacing.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${BORDER_RADIUS};
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.25);
  max-width: 600px;
  border-top: ${BORDER_RADIUS} solid ${({ theme }) => theme.colors.success};

  h3 {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs}rem;
    padding-right: ${({ theme }) => theme.spacing.lg}rem;

    svg {
      color: ${({ theme }) => theme.colors.success};
    }
  }

  p {
    margin: 0 0 ${({ theme }) => theme.spacing.sm}rem;
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.metalGray};
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.sm}rem;
  right: ${({ theme }) => theme.spacing.sm}rem;
  display: flex;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.iconGray};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs}rem;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.menuBg};
  }
`
